import React, { ReactNode } from "react"
import { graphql, useStaticQuery, Link } from "gatsby"
import SanityImage from "gatsby-plugin-sanity-image"
import {
  Navbar,
  Footer,
  SocialMediaIcon,
  getServiceByUrl,
} from "@gorilla-technologies/customer-ui"
import classNames from "classnames"
import { DocumentHead, Wallet, ColorPicker } from "."

export type SanityImageProps = {
  [x: string]: any
  asset: any
  hotspot: any
  crop: any
  width: any
  height: any
  options?: {}
  config?: {}
  __typename: any
  _type: any
  _key: any
  sources: any
}

type SanityNavItem = {
  title: string
  slug: {
    current: string
  }
}

export type LayoutProps = {
  children: ReactNode
  removeTopPadding?: boolean
  title: string
  metaDescription?: string
  navbarBgColor?: "gray" | "brand"
}

type LayoutQueryResults = {
  sanityConfig: {
    companyName?: string
    hideCompanyName?: boolean
    socialMediaLinks: string[]
    logo?: SanityImageProps
    logoSize: "small" | "medium" | "large"
    primaryColor?: {
      title: string
    }
  }
  sanityNavigation: {
    headerMenu: SanityNavItem[]
    footerMenu: SanityNavItem[]
  }
}

// TODO Fetch colors differntly than by title
const getHueByColorTitle = colorTitle => {
  switch (colorTitle) {
    case "Red":
      return 0
    case "Blue":
      return 215
    case "Green":
      return 169
    case "Yellow":
      return 46
    case "Purple":
    default:
      return 250
  }
}

const isSSR = typeof window === "undefined"

export const Layout = ({
  children,
  title,
  metaDescription,
  navbarBgColor,
  removeTopPadding = false,
}: LayoutProps) => {
  const { sanityConfig, sanityNavigation } =
    useStaticQuery<LayoutQueryResults>(graphql`
      query LayoutStatic {
        sanityConfig {
          companyName
          hideCompanyName
          socialMediaLinks
          logo {
            ...ImageWithPreview
            asset {
              path
            }
          }
          logoSize
          primaryColor {
            title
          }
        }
        sanityNavigation {
          headerMenu {
            title
            slug {
              current
            }
          }
          footerMenu {
            title
            slug {
              current
            }
          }
        }
      }
    `)

  // Destructure Query Results
  const { companyName, hideCompanyName, logo, socialMediaLinks, primaryColor } =
    sanityConfig
  const { footerMenu, headerMenu } = sanityNavigation

  // Set brand color
  const hue = getHueByColorTitle(primaryColor?.title)
  if (!isSSR)
    document.documentElement.style.setProperty("--color-brand-hue", String(hue))

  const contentClassName = classNames({
    "flex flex-col grow gap-8 items-center pb-8 lg:gap-20 lg:pb-36": true,
    "pt-8 lg:pt-20": removeTopPadding === false,
  })

  const showColorPicker = isSSR
    ? false
    : new URLSearchParams(window.location.search).has("colors")

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <DocumentHead
        title={`${title} | ${companyName}`}
        description={
          metaDescription ||
          `${companyName} is an NFT shop built with GorillaShops`
        }
        logo={logo && `https://cdn.sanity.io/${logo.asset.path}`}
      />

      {showColorPicker && <ColorPicker />}
      <Navbar
        title={companyName}
        hideTitle={hideCompanyName}
        bgColor={navbarBgColor}
        links={[
          <Link
            to="/products/1"
            className="text-base font-semibold text-gray-500 antialiased hover:text-gray-900 md:text-lg md:leading-[1.625rem]"
            activeClassName="text-gray-900"
            key="/products/1"
          >
            All products
          </Link>,
          ...headerMenu.map(navItem => (
            <Link
              to={`/${navItem.slug.current}`}
              className="text-base font-semibold text-gray-500 antialiased hover:text-gray-900 md:text-lg md:leading-[1.625rem]"
              activeClassName="text-gray-900"
              key={navItem.slug.current}
            >
              {navItem.title}
            </Link>
          )),
        ]}
        buttons={[<Wallet key="wallet" />]}
        logo={
          logo && (
            <SanityImage
              {...logo}
              alt="logo"
              height={48}
              style={{
                height: "100%",
              }}
            />
          )
        }
      />
      <div className={contentClassName}>{children}</div>
      <Footer
        socials={socialMediaLinks.map(url => (
          <a
            href={url}
            className="p-1.5 text-base font-normal leading-7 text-white antialiased hover:underline"
            key={url}
            title={getServiceByUrl(url)}
            target="_blank"
            rel="noreferrer"
          >
            <SocialMediaIcon url={url} />
          </a>
        ))}
        links={footerMenu.map(navItem => (
          <Link
            to={`/${navItem.slug.current}`}
            className="text-sm leading-5 text-white antialiased hover:underline"
            activeClassName="underline"
            key={navItem.slug.current}
          >
            {navItem.title}
          </Link>
        ))}
      />
    </div>
  )
}
